import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import AdminNavBar from "../components/AdminNavbar";
import "../assets/css/viewstudents.css";

const API =
  process.env.NODE_ENV === "production"
    ? process.env.REACT_APP_API_BASE_URL || "https://schools.luth.gov.ng"
    : "http://localhost:3003";

const PROGRAMME_OPTIONS = [
  "Community Health II",
  "Community I",
  "Accident and Emergency",
  "Critical Care",
  "Dental Nursing",
  "Neonatal Nursing",
  "Nephrology Nursing",
  "Nursing Education",
  "Oncology Nursing",
  "Ophthalmic Nursing",
  "Paediatric Nursing",
  "Perioperative Nursing",
  "Federal School of Biomedical Engineering",
  "General Nursing",
  "Health Information Management",
  "Medical and Psychiatry Social Work",
];

const safeText = (value, fallback = "N/A") =>
  value === null || value === undefined || String(value).trim() === ""
    ? fallback
    : value;

const getPhotoSrc = (picture) => {
  if (picture && String(picture).trim() !== "") return picture;
  return "https://via.placeholder.com/240x240.png?text=No+Photo";
};

const normalizeProgramme = (value) => {
  const text = safeText(value, "");
  const found = PROGRAMME_OPTIONS.find(
    (p) => p.toLowerCase() === String(text).toLowerCase(),
  );
  return found || text;
};

const StudentCard = ({ student, onViewProfile }) => {
  const photoSrc = getPhotoSrc(student.picture);

  return (
    <div className="student-card">
      {" "}
      <div className="student-card-top">
        {" "}
        <img
          className="student-photo"
          src={photoSrc}
          alt={student.fullName}
        />{" "}
        <div className="student-meta">
          {" "}
          <h3>{safeText(student.fullName, "Student Name")}</h3>{" "}
          <p className="matric">{safeText(student.matricNumber)}</p>{" "}
          <span className="programme-pill">
            {safeText(student.department)}{" "}
          </span>{" "}
        </div>{" "}
      </div>
      <div className="student-card-body">
        <div className="info-row">
          <span>Gender</span>
          <strong>{safeText(student.gender)}</strong>
        </div>
        <div className="info-row">
          <span>Level</span>
          <strong>{safeText(student.Level)}</strong>
        </div>
        <div className="info-row">
          <span>Session</span>
          <strong>{safeText(student.session)}</strong>
        </div>
        <div className="info-row">
          <span>Entry Mode</span>
          <strong>{safeText(student.entryMode)}</strong>
        </div>
        <div className="info-row">
          <span>Phone</span>
          <strong>{safeText(student.phoneNumber)}</strong>
        </div>
        <div className="info-row">
          <span>Email</span>
          <strong className="email-text">{safeText(student.email)}</strong>
        </div>
      </div>
      <div className="student-card-footer">
        <div className="footer-mini">
          <span>Calendar Year</span>
          <strong>{safeText(student.CalenderYear)}</strong>
        </div>
        <div className="footer-mini">
          <span>Matric No</span>
          <strong>{safeText(student.matricNumber)}</strong>
        </div>
      </div>
      <button
        type="button"
        className="view-profile-btn"
        onClick={() => onViewProfile(student)}
      >
        View Profile
      </button>
    </div>
  );
};

const DetailItem = ({ label, value }) => (
  <div className="profile-detail-item">
    <span>{label}</span>
    <strong>{safeText(value)}</strong>
  </div>
);

const ProfileModal = ({ student, onClose }) => {
  if (!student) return null;

  const photoSrc = getPhotoSrc(student.picture);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="profile-modal" onClick={(e) => e.stopPropagation()}>
        {" "}
        <div className="profile-modal-header">
          {" "}
          <div className="profile-head-left">
            {" "}
            <img
              className="profile-photo"
              src={photoSrc}
              alt={student.fullName}
            />{" "}
            <div>
              {" "}
              <p className="profile-title">Student Profile</p>{" "}
              <h2>{safeText(student.fullName)}</h2>{" "}
              <p className="profile-subtitle">
                {safeText(student.matricNumber)}
              </p>{" "}
            </div>{" "}
          </div>
          <button type="button" className="modal-close-btn" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="profile-badge-row">
          <span className="profile-badge">{safeText(student.department)}</span>
          <span className="profile-badge">{safeText(student.Level)}</span>
          <span className="profile-badge">{safeText(student.session)}</span>
        </div>
        <div className="profile-grid">
          <DetailItem label="Full Name" value={student.fullName} />
          <DetailItem label="Date of Birth" value={student.dateOfBirth} />
          <DetailItem label="Gender" value={student.gender} />
          <DetailItem label="Nationality" value={student.nationality} />
          <DetailItem label="State of Origin" value={student.stateOfOrigin} />
          <DetailItem label="LGA" value={student.lga} />
          <DetailItem label="Religion" value={student.religion} />
          <DetailItem label="Marital Status" value={student.maritalStatus} />
          <DetailItem label="Phone Number" value={student.phoneNumber} />
          <DetailItem label="Email" value={student.email} />
          <DetailItem label="Address" value={student.address} />
          <DetailItem label="JAMB Reg Number" value={student.jambRegNumber} />
          <DetailItem label="Matric Number" value={student.matricNumber} />
          <DetailItem label="Next of Kin Name" value={student.nextOfKinName} />
          <DetailItem
            label="Next of Kin Phone"
            value={student.nextOfKinPhone}
          />
          <DetailItem
            label="Next of Kin Address"
            value={student.nextOfKinAddress}
          />
          <DetailItem
            label="Next of Kin Relationship"
            value={student.nextOfKinRelationship}
          />
          <DetailItem
            label="Next of Kin Email"
            value={student.nextOfKinEmail}
          />
          <DetailItem label="Primary School" value={student.primarySchool} />
          <DetailItem
            label="Primary Grad Year"
            value={student.primaryGradYear}
          />
          <DetailItem
            label="Primary Qualification"
            value={student.primaryQualification}
          />
          <DetailItem
            label="Secondary School"
            value={student.secondarySchool}
          />
          <DetailItem
            label="Secondary Grad Year"
            value={student.secondaryGradYear}
          />
          <DetailItem
            label="Secondary Qualification"
            value={student.secondaryQualification}
          />
          <DetailItem
            label="Post Secondary School"
            value={student.postSecondarySchool}
          />
          <DetailItem
            label="Post Secondary Grad Year"
            value={student.postSecondaryGradYear}
          />
          <DetailItem
            label="Post Secondary Qualification"
            value={student.postSecondaryQualification}
          />
          <DetailItem label="Sponsor Name" value={student.sponsorName} />
          <DetailItem label="Sponsor Phone 1" value={student.sponsorPhone1} />
          <DetailItem label="Sponsor Phone 2" value={student.sponsorPhone2} />
          <DetailItem label="Sponsor Email" value={student.sponsorEmail} />
          <DetailItem label="Sponsor Address" value={student.sponsorAddress} />
          <DetailItem label="Programme" value={student.department} />
          <DetailItem label="Level" value={student.Level} />
          <DetailItem label="Entry Mode" value={student.entryMode} />
          <DetailItem label="Entry Year" value={student.entryYear} />
          <DetailItem label="Session" value={student.session} />
          <DetailItem label="Semester" value={student.Semester} />
          <DetailItem
            label="Other Qualification"
            value={student.otherQualification}
          />
          <DetailItem
            label="Other Qualification Year"
            value={student.otherQualificationYear}
          />
          <DetailItem
            label="Other Qualification School"
            value={student.otherQualificationSchool}
          />
          <DetailItem label="Calendar Year" value={student.CalenderYear} />
          <DetailItem label="Created At" value={student.createdAt} />
        </div>
      </div>
    </div>
  );
};

export default function ViewStudentsPage() {
  const [students, setStudents] = useState([]);
  const [selectedProgramme, setSelectedProgramme] = useState("All");
  const [selectedLevel, setSelectedLevel] = useState("All");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState("");
  const [selectedProfile, setSelectedProfile] = useState(null);

  const token = localStorage.getItem("token");

  useEffect(() => {
    let cancelled = false;

    const loadStudents = async () => {
      try {
        setLoading(true);
        setPageError("");

        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await axios.get(`${API}/api/students-view-data`, {
          headers,
          params: {
            programme: selectedProgramme,
            level: selectedLevel,
          },
        });

        if (!cancelled) {
          setStudents(Array.isArray(res.data) ? res.data : []);
        }
      } catch (err) {
        console.error("Failed to load students:", err);
        if (!cancelled) {
          setStudents([]);
          setPageError("Some student data could not be loaded.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadStudents();

    return () => {
      cancelled = true;
    };
  }, [selectedProgramme, selectedLevel, token]);

  const filteredStudents = useMemo(() => {
    const q = search.trim().toLowerCase();

    return students.filter((s) => {
      const name = String(s.fullName || "").toLowerCase();
      const matric = String(s.matricNumber || "").toLowerCase();
      return name.includes(q) || matric.includes(q);
    });
  }, [students, search]);

  const currentStudents = filteredStudents;

  const totalStudents = currentStudents.length;
  const maleCount = currentStudents.filter(
    (s) => String(s.gender).toLowerCase() === "male",
  ).length;
  const femaleCount = currentStudents.filter(
    (s) => String(s.gender).toLowerCase() === "female",
  ).length;

  const availableLevels = useMemo(() => {
    const levels = [
      ...new Set(
        students.map((s) => String(s.Level || "").trim()).filter(Boolean),
      ),
    ];
    return levels.sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true }),
    );
  }, [students]);

  const exportToExcel = () => {
    const exportRows = currentStudents.map((student, index) => ({
      No: index + 1,
      "Full Name": safeText(student.fullName),
      "Matric Number": safeText(student.matricNumber),
      Gender: safeText(student.gender),
      Nationality: safeText(student.nationality),
      "State of Origin": safeText(student.stateOfOrigin),
      LGA: safeText(student.lga),
      Religion: safeText(student.religion),
      "Marital Status": safeText(student.maritalStatus),
      "Phone Number": safeText(student.phoneNumber),
      Email: safeText(student.email),
      Address: safeText(student.address),
      "JAMB Reg Number": safeText(student.jambRegNumber),
      Programme: safeText(student.department),
      Level: safeText(student.Level),
      "Entry Mode": safeText(student.entryMode),
      "Entry Year": safeText(student.entryYear),
      Session: safeText(student.session),
      Semester: safeText(student.Semester),
      "Calendar Year": safeText(student.CalenderYear),
      "Next of Kin Name": safeText(student.nextOfKinName),
      "Next of Kin Phone": safeText(student.nextOfKinPhone),
      "Sponsor Name": safeText(student.sponsorName),
      "Sponsor Phone 1": safeText(student.sponsorPhone1),
      "Sponsor Email": safeText(student.sponsorEmail),
    }));

    const ws = XLSX.utils.json_to_sheet(exportRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Students");

    const programmeName =
      selectedProgramme && selectedProgramme !== "All"
        ? selectedProgramme.replace(/[\\/:*?"<>|]/g, "-")
        : "All_Programmes";

    const levelName =
      selectedLevel && selectedLevel !== "All"
        ? `_Level_${selectedLevel.replace(/[\\/:*?"<>|]/g, "-")}`
        : "";

    XLSX.writeFile(wb, `Students_${programmeName}${levelName}.xlsx`);
  };

  return (
    <>
      {" "}
      <AdminNavBar />{" "}
      <div className="students-page">
        {" "}
        <div className="students-main">
          {" "}
          <div className="page-hero">
            {" "}
            <div>
              {" "}
              <p className="eyebrow">Students Directory</p>{" "}
              <h1>View all student records</h1>{" "}
              <p className="hero-text">
                Search by name or matric number, filter by programme and level,
                and export the current list to Excel.{" "}
              </p>{" "}
            </div>
            <div className="hero-actions">
              <button
                type="button"
                className="export-btn"
                onClick={exportToExcel}
                disabled={currentStudents.length === 0}
              >
                Print List to Excel
              </button>
            </div>
          </div>
          <div className="stats-grid">
            <div className="stat-card stat-blue">
              <span className="stat-label">Total Students</span>
              <strong>{totalStudents}</strong>
            </div>
            <div className="stat-card stat-green">
              <span className="stat-label">Male</span>
              <strong>{maleCount}</strong>
            </div>
            <div className="stat-card stat-pink">
              <span className="stat-label">Female</span>
              <strong>{femaleCount}</strong>
            </div>
            <div className="stat-card stat-purple">
              <span className="stat-label">Programmes</span>
              <strong>{PROGRAMME_OPTIONS.length}</strong>
            </div>
          </div>
          <div className="filter-panel">
            <div className="filter-group search-group">
              <label htmlFor="search">Search by name or matric number</label>
              <input
                id="search"
                type="text"
                placeholder="Type a name or matric number"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className="filter-group programme-group">
              <label htmlFor="programme">Programme</label>
              <select
                id="programme"
                value={selectedProgramme}
                onChange={(e) => setSelectedProgramme(e.target.value)}
              >
                <option value="All">All Programmes</option>
                {PROGRAMME_OPTIONS.map((programme, index) => (
                  <option key={`${programme}-${index}`} value={programme}>
                    {programme}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group level-group">
              <label htmlFor="level">Level</label>
              <select
                id="level"
                value={selectedLevel}
                onChange={(e) => setSelectedLevel(e.target.value)}
              >
                <option value="All">All Levels</option>
                {availableLevels.map((level) => (
                  <option key={level} value={level}>
                    {level}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {pageError ? <p className="page-error">{pageError}</p> : null}
          <div className="results-bar">
            <span>
              Showing <strong>{currentStudents.length}</strong> student(s)
            </span>
            <span className="results-tag">
              {selectedProgramme === "All"
                ? "All Programmes"
                : selectedProgramme}
              {" • "}
              {selectedLevel === "All" ? "All Levels" : selectedLevel}
            </span>
          </div>
          {loading ? (
            <div className="loading-wrap">
              <div className="loading-spinner" />
              <p>Loading students...</p>
            </div>
          ) : currentStudents.length === 0 ? (
            <div className="empty-state">
              <h3>No students now</h3>
              <p>
                There are no students in this programme and level yet. Try
                another programme or level.
              </p>
            </div>
          ) : (
            <div className="students-grid">
              {currentStudents.map((student, index) => (
                <StudentCard
                  key={student.id || student.matricNumber || index}
                  student={student}
                  onViewProfile={setSelectedProfile}
                />
              ))}
            </div>
          )}
        </div>
        {selectedProfile ? (
          <ProfileModal
            student={selectedProfile}
            onClose={() => setSelectedProfile(null)}
          />
        ) : null}
      </div>
    </>
  );
}
